import type { ComegoAdd, ComegoTime, ComegoUpdate } from "../models/comego";
import { type ApiClient, createEntityStore, runStoreAction } from "./shared";

export type WorkinghoursEntity = ComegoTime;

export const createWorkinghoursStore = (apiClient: ApiClient) => {
	const useStore = createEntityStore<WorkinghoursEntity>();
	const getBetween = async (
		startIso: string,
		endIso: string,
		userId?: string
	): Promise<readonly WorkinghoursEntity[]> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const filter = `start ge '${startIso}' and start le '${endIso}'${userId ? ` and user__id eq '${userId}'` : ""}`;
			const items = await apiClient.get<WorkinghoursEntity[]>("/get/comego", {
				$filter: filter,
			});
			useStore.getState().upsertMany(items);
			return items;
		});
	};

	const findRunning = async (userId?: string, take = 1): Promise<readonly WorkinghoursEntity[] | null> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const items = await apiClient.get<WorkinghoursEntity[]>("/get/comego", {
				$filter: `user__id eq '${userId}' and end eq null`,
				$top: take,
				$orderby: "start desc",
			});
			return items ?? null;
		});
	};

	const add = async (entries: readonly ComegoAdd[]): Promise<readonly WorkinghoursEntity[]> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const response = await apiClient.post<WorkinghoursEntity[] | WorkinghoursEntity>("/add/comego", entries);
			const items = Array.isArray(response) ? response : [response];
			useStore.getState().upsertMany(items);
			return items;
		});
	};

	const update = async (entries: readonly ComegoUpdate[]): Promise<readonly WorkinghoursEntity[]> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const response = await apiClient.post<WorkinghoursEntity[] | WorkinghoursEntity>("/update/comego", entries);
			const items = Array.isArray(response) ? response : [response];
			useStore.getState().upsertMany(items);
			return items;
		});
	};

	const updateDay = async (input: {
		readonly date: string;
		readonly payload: readonly (ComegoAdd | ComegoUpdate)[];
		readonly userId?: string;
	}): Promise<{ deletedIds: readonly string[]; items: readonly WorkinghoursEntity[] }> => {
		const state = useStore.getState();
		return runStoreAction(state.setLoading, state.setError, async () => {
			const response = await apiClient.post<{
				deletedIds?: readonly string[];
				items?: readonly WorkinghoursEntity[];
			}>("/workinghours/days", input);
			const items = response.items ?? [];
			const deletedIds = response.deletedIds ?? [];
			if (deletedIds.length > 0) {
				useStore.getState().removeMany(deletedIds);
			}
			if (items.length > 0) {
				useStore.getState().upsertMany(items);
			}
			return { items, deletedIds };
		});
	};

	const remove = async (ids: readonly string[]): Promise<void> => {
		const state = useStore.getState();
		await runStoreAction(state.setLoading, state.setError, async () => {
			await apiClient.delete<unknown>(`/delete/comego/${ids.join(";")}`);
			useStore.getState().removeMany(ids);
		});
	};

	return {
		useStore,
		actions: {
			getBetween,
			findRunning,
			add,
			update,
			updateDay,
			delete: remove,
		},
	};
};
